/**
 * Wrapper pour localStorage/sessionStorage avec gestion d'erreurs.
 * Partage entre tous les modules FrontEnd.
 */

type StorageType = 'local' | 'session';

export class StorageManager {
  private getStorage(type: StorageType): Storage | null {
    try {
      const storage = type === 'local' ? window.localStorage : window.sessionStorage;
      const testKey = '__storage_test__';
      storage.setItem(testKey, 'test');
      storage.removeItem(testKey);
      return storage;
    } catch {
      console.warn(`${type}Storage n'est pas disponible`);
      return null;
    }
  }

  setItem(key: string, value: unknown, type: StorageType = 'local'): boolean {
    try {
      const storage = this.getStorage(type);
      if (!storage) return false;
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }

  getItem<T = unknown>(key: string, type: StorageType = 'local'): T | null {
    try {
      const storage = this.getStorage(type);
      if (!storage) return null;
      const item = storage.getItem(key);
      if (!item) return null;
      return JSON.parse(item) as T;
    } catch {
      return null;
    }
  }

  removeItem(key: string, type: StorageType = 'local'): boolean {
    try {
      const storage = this.getStorage(type);
      if (!storage) return false;
      storage.removeItem(key);
      return true;
    } catch {
      return false;
    }
  }

  clear(type: StorageType = 'local'): boolean {
    try {
      const storage = this.getStorage(type);
      if (!storage) return false;
      storage.clear();
      return true;
    } catch {
      return false;
    }
  }

  hasItem(key: string, type: StorageType = 'local'): boolean {
    const storage = this.getStorage(type);
    if (!storage) return false;
    return storage.getItem(key) !== null;
  }
}

/** Instance singleton */
export const storage = new StorageManager();
